/**
 * Thin fetch wrapper used by every call to ops-api.
 *
 * Responsibilities:
 *   - Attach `credentials: 'include'` so the session cookie round-trips on
 *     cross-origin calls (`ops.*` → `ops.api.*`).
 *   - Default JSON content-type on bodied requests.
 *   - Normalize non-2xx responses into `OpsApiError` carrying the ADR-004
 *     error envelope so callers don't each re-parse it.
 *   - Forward network-level failures as a `SERVICE_UNAVAILABLE`-shaped
 *     error so UI code can branch on `.code` uniformly.
 *
 * Base URL comes from `import.meta.env.VITE_OPS_API_BASE_URL` at build
 * time. If that env is missing, browser-only fallback logic derives the
 * matching ops-api hostname from the current Pages hostname, or uses the
 * current local dev hostname with the ops-api worker port.
 */

const LOCAL_OPS_API_PORT = 8787;
const LOCAL_HOST_PARTS = ['local', 'host'] as const;

interface ErrorEnvelope {
  error: {
    code: string;
    message: string;
    retryable: boolean;
  };
  /** Full raw `error` object, preserving code-specific extra fields. */
  details: Record<string, unknown>;
  /** The whole parsed body, for codes that carry SIBLINGS of `error`. */
  body: Record<string, unknown>;
}

export class OpsApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryable: boolean;
  /**
   * Full parsed `error` object from the ADR-004 envelope. Some error
   * codes carry structured payloads beyond code/message/retryable —
   * e.g. `CASCADE_CONFIRMATION_REQUIRED` embeds `cascade_impact` so the
   * UI can render the blast radius before the operator confirms.
   */
  readonly details: Record<string, unknown> | null;
  /**
   * The whole parsed error body, not just its `error` member.
   *
   * Most codes hang their structured payload INSIDE `error`, which `details`
   * already covers. `FIREWALL_RULESET_INVALID` does not: the gateway relays
   * `issues` as a SIBLING of `error` (`writeFailure` in
   * `internal-firewall-rulesets.ts`), and that list is the entire point of the
   * 400 — it is how a draft with four mistakes gets fixed in one pass instead
   * of four round trips. Dropping it here would push every consumer into
   * hand-rolling its own fetch just to read the body.
   */
  readonly body: Record<string, unknown> | null;
  constructor(
    status: number,
    code: string,
    message: string,
    retryable: boolean,
    details: Record<string, unknown> | null = null,
    body: Record<string, unknown> | null = null,
  ) {
    super(message);
    this.name = 'OpsApiError';
    this.status = status;
    this.code = code;
    this.retryable = retryable;
    this.details = details;
    this.body = body;
  }
}

export interface OpsApiRequestInit {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export function getOpsApiBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_OPS_API_BASE_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) {
    return fromEnv.replace(/\/+$/, '');
  }
  return inferOpsApiBaseUrl();
}

/**
 * Issue a request against ops-api. Returns parsed JSON body (typed by
 * caller). Throws `OpsApiError` on any non-2xx.
 *
 * Kept deliberately tiny — no caching, no retries, no interceptor stack.
 * Wave 5 can layer higher-level client code on top without reshaping this.
 */
export async function opsApiRequest<T>(
  path: string,
  init: OpsApiRequestInit = {},
  fetchImpl: typeof fetch = fetch,
): Promise<T> {
  const baseUrl = getOpsApiBaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = `${baseUrl}${normalizedPath}`;

  const headers: Record<string, string> = { ...(init.headers ?? {}) };
  let body: BodyInit | undefined;
  if (init.body !== undefined) {
    body = JSON.stringify(init.body);
    if (headers['Content-Type'] === undefined && headers['content-type'] === undefined) {
      headers['Content-Type'] = 'application/json';
    }
  }

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: init.method ?? (body === undefined ? 'GET' : 'POST'),
      credentials: 'include',
      headers,
      body,
      signal: init.signal,
    });
  } catch (cause) {
    const message = cause instanceof Error ? cause.message : 'network request failed';
    throw new OpsApiError(0, 'SERVICE_UNAVAILABLE', message, true);
  }

  const rawText = await response.text();
  const parsed = rawText.length === 0 ? null : safeJsonParse(rawText);

  if (!response.ok) {
    const envelope = asErrorEnvelope(parsed);
    if (envelope !== null) {
      throw new OpsApiError(
        response.status,
        envelope.error.code,
        envelope.error.message,
        envelope.error.retryable,
        envelope.details,
        envelope.body,
      );
    }
    throw new OpsApiError(
      response.status,
      'SERVICE_UNAVAILABLE',
      `ops-api returned ${response.status} with non-envelope body`,
      response.status >= 500,
    );
  }

  return parsed as T;
}

function safeJsonParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function asErrorEnvelope(raw: unknown): ErrorEnvelope | null {
  if (!isRecord(raw)) {
    return null;
  }
  const error = raw.error;
  if (!isRecord(error)) {
    return null;
  }
  const { code, message, retryable } = error;
  if (typeof code !== 'string' || typeof message !== 'string' || typeof retryable !== 'boolean') {
    return null;
  }
  return {
    error: { code, message, retryable },
    details: error,
    body: raw,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function inferOpsApiBaseUrl(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  const hostname = getWindowHostname();
  const protocol = window.location.protocol;
  if (hostname === 'ops.nadoutong.org') {
    return 'https://ops.api.nadoutong.org';
  }
  if (hostname !== null && hostname.startsWith('ops.') && hostname.endsWith('.nadoutong.org')) {
    return `https://ops.api.${hostname.slice('ops.'.length)}`;
  }

  const normalizedProtocol = protocol === 'https:' ? 'https:' : 'http:';
  const localHost = hostname ?? LOCAL_HOST_PARTS.join('');
  return `${normalizedProtocol}//${localHost}:${LOCAL_OPS_API_PORT}`;
}

function getWindowHostname(): string | null {
  const hostname = window.location.hostname;
  return typeof hostname === 'string' && hostname.length > 0 ? hostname : null;
}
